import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import api from '../../../services/api';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Dialog, DialogContent, DialogTitle } from '../../../components/ui/dialog';
import {
  Eye, EyeOff, CheckCircle2, X, BadgeCheck, Phone, Copy, Send, ShieldCheck,
  Loader2, MessageCircle, ChevronLeft, Clock, Wifi, RefreshCw,
} from 'lucide-react';
import { useTenantTheme } from '../../../contexts/TenantThemeContext';
import { openExternalUrl } from '../../../lib/externalNavigation';
import {
  clearStoredPhoneVerificationAttempt,
  confirmedStoredPhoneVerificationAttempt,
  readStoredPhoneVerificationAttempt,
  resolvePhoneVerified,
  storePhoneVerificationAttempt,
} from '../../../lib/phoneVerification';

const PANEL_ACCENT = 'var(--vl-profile-accent, var(--vl-neon, #22FF88))';
const PANEL_ACCENT_TEXT = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #22FF88)) 70%, #FFFFFF)';
const PANEL_ACCENT_DIM = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #22FF88)) 16%, transparent)';
const PANEL_ACCENT_BORDER = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #22FF88)) 48%, transparent)';
const PANEL_GOLD = 'var(--vl-brand-primary, #D4AF37)';
const PANEL_GOLD_DIM = 'color-mix(in oklab, var(--vl-brand-primary, #D4AF37) 18%, transparent)';
const PANEL_GOLD_BORDER = 'color-mix(in oklab, var(--vl-brand-primary, #D4AF37) 52%, transparent)';
const VERIFICATION_POLL_MS = 2500;

const inputStyle = {
  background: 'color-mix(in oklab, var(--vl-bg, #000000) 86%, #06130D)',
  borderColor: PANEL_ACCENT_BORDER,
  color: '#FFFFFF',
  caretColor: PANEL_ACCENT,
  boxShadow: `0 0 0 1px ${PANEL_ACCENT_DIM}`,
};

const toUsDigits = (value = '') => {
  const digits = String(value).replace(/\D/g, '');
  if (digits.length > 10 && digits.startsWith('1')) return digits.slice(1, 11);
  return digits.slice(0, 10);
};

const formatUsPhone = (digits = '') => {
  const d = toUsDigits(digits);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

const cleanSmsNumber = (value = '') => {
  const digits = String(value).replace(/\D/g, '');
  return digits ? `+${digits}` : '';
};

const formatFp = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return '0 FP';
  const text = Number.isInteger(amount) ? String(amount) : amount.toFixed(2).replace(/\.?0+$/, '');
  return `${text} FP`;
};

const formatCountdown = (seconds) => {
  const safeSeconds = Math.max(0, Number(seconds) || 0);
  const minutes = Math.floor(safeSeconds / 60);
  const remainder = safeSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
};

const phoneVerifiedToastMessage = (status) => {
  const amount = Number(status?.mobile_verification_bonus_amount || status?.phone_verify_reward_fp || 0);
  const rewardEnabled = status?.phone_verify_reward_enabled !== false && amount > 0;
  const rewardAwarded = status?.mobile_verification_bonus_awarded !== false;
  if (rewardEnabled && rewardAwarded) {
    return `Phone verified. ${formatFp(amount)} Freeplay added.`;
  }
  return 'Phone verified.';
};

/**
 * In-app password change form. Validates current/new locally then calls
 * /api/auth/change-password.
 */
export function ResetPasswordPanel({ close }) {
  const [current, setCurrent] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!current || !newPw) { toast.error('Fill all fields'); return; }
    if (newPw !== confirm) { toast.error('New passwords do not match'); return; }
    if (newPw.length < 6) { toast.error('Min 6 characters'); return; }
    setBusy(true);
    try {
      await api.post('/api/auth/change-password', { old_password: current, new_password: newPw });
      toast.success('Password changed');
      close();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs uppercase tracking-wider mb-1 block" style={{ color: PANEL_ACCENT_TEXT }}>Current Password</label>
        <div className="relative">
          <Input type={showCur ? 'text' : 'password'} value={current} onChange={e => setCurrent(e.target.value)} className="pr-10" style={inputStyle} data-testid="reset-current" />
          <button onClick={() => setShowCur(!showCur)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: '#FFFFFF' }}>
            {showCur ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <div>
        <label className="text-xs uppercase tracking-wider mb-1 block" style={{ color: PANEL_ACCENT_TEXT }}>New Password</label>
        <div className="relative">
          <Input type={showNew ? 'text' : 'password'} value={newPw} onChange={e => setNewPw(e.target.value)} className="pr-10" style={inputStyle} data-testid="reset-new" />
          <button onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: '#FFFFFF' }}>
            {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <div>
        <label className="text-xs uppercase tracking-wider mb-1 block" style={{ color: PANEL_ACCENT_TEXT }}>Confirm New Password</label>
        <Input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} style={inputStyle} data-testid="reset-confirm" />
      </div>
      <Button onClick={submit} disabled={busy} className="btn-primary w-full h-11 mt-2" data-testid="reset-submit">
        {busy ? <div className="spinner w-4 h-4" /> : 'Change Password'}
      </Button>
    </div>
  );
}

/**
 * In-app profile editor — full name, username (debounced uniqueness check),
 * phone. Email cannot be changed here.
 */
export function EditProfilePanel({
  user,
  checkAuth,
  close,
  initialVerifyOpen = false,
  verificationBackSignal = 0,
  onVerificationScreenOpen,
  onVerificationScreenClose,
}) {
  const { theme } = useTenantTheme();
  const inboxBrandName = theme?.brand_name || 'your team';
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [username, setUsername] = useState(user?.username || '');
  const [usernameAvail, setUsernameAvail] = useState(null);
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [phoneDigits, setPhoneDigits] = useState(() => toUsDigits(user?.phone_e164 || user?.phone_number || user?.phone || ''));
  const [verifyStatus, setVerifyStatus] = useState(null);
  const [verifyStatusLoading, setVerifyStatusLoading] = useState(true);
  const [verifyStatusError, setVerifyStatusError] = useState(false);
  const [reverseSms, setReverseSms] = useState(null);
  const [reverseBusy, setReverseBusy] = useState(false);
  const [verifyPopupOpen, setVerifyPopupOpen] = useState(false);
  const [verificationPhase, setVerificationPhase] = useState('entry');
  const [verificationError, setVerificationError] = useState('');
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [lastTrackingAt, setLastTrackingAt] = useState(null);
  const [trackingState, setTrackingState] = useState('idle');
  const [busy, setBusy] = useState(false);
  const [storedAttemptAtMount] = useState(() => readStoredPhoneVerificationAttempt(user));
  const [restoreCheckPending, setRestoreCheckPending] = useState(() => Boolean(storedAttemptAtMount));
  const phoneSectionRef = useRef(null);
  const phoneInputRef = useRef(null);
  const verificationCompletedRef = useRef(false);
  const reverseStartInFlightRef = useRef(false);
  const verificationGenerationRef = useRef(0);
  const trackingInFlightRef = useRef(false);
  const restoreAttemptHandledRef = useRef(!storedAttemptAtMount);
  const currentUsername = (user?.username || '').trim();
  const phoneVerified = resolvePhoneVerified(verifyStatus, user);
  const phoneE164 = phoneDigits ? `+1${phoneDigits}` : '';
  const activeReverseAttempt = Boolean(reverseSms && verificationPhase === 'waiting');

  const openVerificationScreen = () => {
    onVerificationScreenOpen?.();
    setVerifyPopupOpen(true);
  };

  const closeVerificationScreen = (afterClose) => {
    const closeLocal = () => {
      setVerifyPopupOpen(false);
      afterClose?.();
    };
    if (onVerificationScreenClose) {
      onVerificationScreenClose(closeLocal);
    } else {
      closeLocal();
    }
  };

  const fetchVerifyStatus = useCallback(async ({ cacheBust = false } = {}) => {
    try {
      const r = await api.get('/api/users/me/phone-verification/status', cacheBust ? {
        params: { _ts: Date.now() },
        headers: { 'Cache-Control': 'no-cache' },
      } : undefined);
      return r.data || null;
    } catch {
      return null;
    }
  }, []);

  const refreshVerifyStatus = useCallback(async () => {
    const requestGeneration = verificationGenerationRef.current;
    setVerifyStatusLoading(true);
    const status = await fetchVerifyStatus({ cacheBust: true });
    if (verificationGenerationRef.current !== requestGeneration) {
      setVerifyStatusLoading(false);
      return null;
    }
    if (status) {
      setVerifyStatus(status);
      setVerifyStatusError(false);
    } else {
      setVerifyStatusError(true);
    }
    setVerifyStatusLoading(false);
    return status;
  }, [fetchVerifyStatus]);

  useEffect(() => {
    let cancelled = false;
    const requestGeneration = verificationGenerationRef.current;
    void fetchVerifyStatus({ cacheBust: true }).then((status) => {
      if (cancelled) return;
      if (verificationGenerationRef.current !== requestGeneration) {
        setVerifyStatusLoading(false);
        return;
      }
      if (status) {
        setVerifyStatus(status);
        setVerifyStatusError(false);
      } else {
        setVerifyStatusError(true);
      }
      setVerifyStatusLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchVerifyStatus]);

  useEffect(() => {
    if (restoreAttemptHandledRef.current || !verifyStatus) return;
    restoreAttemptHandledRef.current = true;
    setRestoreCheckPending(false);

    const confirmed = confirmedStoredPhoneVerificationAttempt(storedAttemptAtMount, verifyStatus);
    if (!confirmed || reverseSms || verificationPhase !== 'entry') {
      clearStoredPhoneVerificationAttempt(user);
      return;
    }

    const attemptGeneration = verificationGenerationRef.current + 1;
    verificationGenerationRef.current = attemptGeneration;
    const restoredAttempt = {
      ...confirmed.attempt,
      client_started_at: confirmed.attempt.client_started_at
        || confirmed.attempt.submitted_at
        || new Date().toISOString(),
      client_deadline_ms: Date.now() + confirmed.ttlSeconds * 1000,
      client_generation: attemptGeneration,
    };
    setPhoneDigits(toUsDigits(restoredAttempt.phone_e164));
    setReverseSms(restoredAttempt);
    setSecondsRemaining(confirmed.ttlSeconds);
    setLastTrackingAt(null);
    setTrackingState('connecting');
    setVerificationError('');
    setVerificationPhase('waiting');
    storePhoneVerificationAttempt(user, restoredAttempt);
  }, [storedAttemptAtMount, user, verificationPhase, verifyStatus, reverseSms]);

  useEffect(() => {
    if (!phoneVerified) return;
    restoreAttemptHandledRef.current = true;
    setRestoreCheckPending(false);
    clearStoredPhoneVerificationAttempt(user);
  }, [phoneVerified, user]);

  useEffect(() => {
    if (!initialVerifyOpen || phoneVerified) return;
    const focusPhone = () => {
      phoneSectionRef.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      phoneInputRef.current?.focus?.({ preventScroll: true });
    };
    const timers = [80, 220, 420].map(delay => window.setTimeout(focusPhone, delay));
    return () => timers.forEach(timer => window.clearTimeout(timer));
  }, [initialVerifyOpen, phoneVerified]);

  useEffect(() => {
    if (!verificationBackSignal) return;
    setVerifyPopupOpen(false);
  }, [verificationBackSignal]);

  useEffect(() => {
    if (!phoneVerified) return;
    const verifiedDigits = toUsDigits(
      reverseSms?.phone_e164
      || user?.phone_e164
      || user?.phone_number
      || user?.phone
      || '',
    );
    if (verifiedDigits.length === 10) setPhoneDigits(verifiedDigits);
  }, [
    phoneVerified,
    reverseSms?.phone_e164,
    user?.phone,
    user?.phone_e164,
    user?.phone_number,
  ]);

  useEffect(() => {
    if (currentUsername) {
      setUsernameAvail(null);
      setUsernameChecking(false);
      return undefined;
    }
    const current = (user?.username || '').toLowerCase();
    const next = (username || '').trim().toLowerCase();
    if (!next || next === current) { setUsernameAvail(null); return; }
    if (next.length < 3 || !/^[a-zA-Z0-9_]+$/.test(next)) { setUsernameAvail(false); return; }
    setUsernameChecking(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/api/auth/username/available?username=${encodeURIComponent(next)}`);
        setUsernameAvail(!!r.data?.available);
      } catch { setUsernameAvail(null); }
      finally { setUsernameChecking(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [username, user?.username, currentUsername]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.patch('/api/auth/profile', {
        full_name: fullName.trim(),
      });
      const nextU = (username || '').trim();
      if (!currentUsername && nextU && nextU.toLowerCase() !== (user?.username || '').toLowerCase()) {
        await api.post('/api/auth/username/change', { new_username: nextU });
        toast.success(`Username set to @${nextU}`);
      } else {
        toast.success('Profile updated');
      }
      await checkAuth();
      close();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to save');
    } finally { setBusy(false); }
  };

  const usernameChanged = !currentUsername && (username || '').toLowerCase() !== (user?.username || '').toLowerCase();

  const copyText = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Copy failed');
    }
  };

  const startReverseSms = async () => {
    if (reverseBusy || reverseStartInFlightRef.current) return;
    if (restoreCheckPending) return;
    if (phoneDigits.length !== 10) {
      toast.error('Enter your 10 digit US phone number');
      phoneInputRef.current?.focus?.();
      return;
    }
    restoreAttemptHandledRef.current = true;
    setRestoreCheckPending(false);
    clearStoredPhoneVerificationAttempt(user);
    verificationCompletedRef.current = false;
    reverseStartInFlightRef.current = true;
    const attemptGeneration = verificationGenerationRef.current + 1;
    verificationGenerationRef.current = attemptGeneration;
    setVerificationError('');
    setVerificationPhase('starting');
    setReverseBusy(true);
    try {
      const r = await api.post('/api/users/me/phone-verification/reverse/start', { phone: phoneE164 });
      if (verificationGenerationRef.current !== attemptGeneration) return;
      const nextAttempt = r.data || {};
      const initialSeconds = Number(nextAttempt.expires_in_seconds);
      const parsedExpiry = Date.parse(nextAttempt.expires_at || '');
      const ttlSeconds = Number.isFinite(initialSeconds) && initialSeconds > 0
        ? Math.ceil(initialSeconds)
        : Number.isFinite(parsedExpiry)
          ? Math.max(0, Math.ceil((parsedExpiry - Date.now()) / 1000))
          : 600;
      const activeAttempt = {
        ...nextAttempt,
        client_started_at: new Date().toISOString(),
        client_deadline_ms: Date.now() + ttlSeconds * 1000,
        client_generation: attemptGeneration,
      };
      storePhoneVerificationAttempt(user, activeAttempt);
      setReverseSms(activeAttempt);
      setSecondsRemaining(ttlSeconds);
      setLastTrackingAt(null);
      setTrackingState('connecting');
      setVerificationPhase('waiting');
      openVerificationScreen();
    } catch (err) {
      if (verificationGenerationRef.current !== attemptGeneration) return;
      setVerificationPhase('entry');
      toast.error(err.response?.data?.detail || 'Could not start SMS verification');
    } finally {
      reverseStartInFlightRef.current = false;
      setReverseBusy(false);
    }
  };

  useEffect(() => {
    if (!reverseSms || verificationPhase !== 'waiting') return undefined;
    const parsedExpiry = Number(reverseSms.client_deadline_ms) || Date.parse(reverseSms.expires_at || '');
    const fallbackDeadline = Date.now() + Math.max(0, secondsRemaining) * 1000;
    const updateCountdown = () => {
      const deadline = Number.isFinite(parsedExpiry) ? parsedExpiry : fallbackDeadline;
      const next = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setSecondsRemaining(next);
      if (next <= 0) {
        clearStoredPhoneVerificationAttempt(user);
        setVerificationPhase('expired');
        setVerificationError('This code expired without a confirmed verification. Request a new code and try again.');
      }
    };
    updateCountdown();
    const timer = window.setInterval(updateCountdown, 1000);
    return () => window.clearInterval(timer);
    // secondsRemaining is deliberately captured once as the fallback TTL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reverseSms, verificationPhase, user]);

  const checkVerification = useCallback(async () => {
    if (!reverseSms || verificationPhase !== 'waiting' || trackingInFlightRef.current) return;
    const attemptGeneration = reverseSms.client_generation;
    trackingInFlightRef.current = true;
    try {
      const status = await fetchVerifyStatus({ cacheBust: true });
      if (verificationGenerationRef.current !== attemptGeneration) return;
      if (!status) {
        setTrackingState('reconnecting');
        return;
      }
      setVerifyStatus(status);
      setLastTrackingAt(Date.now());
      setTrackingState('live');
      if (status.verified && !verificationCompletedRef.current) {
        verificationCompletedRef.current = true;
        clearStoredPhoneVerificationAttempt(user);
        setVerificationPhase('verified');
        setVerificationError('');
        toast.success(phoneVerifiedToastMessage(status));
        await checkAuth?.();
        return;
      }
      const rejected = status.last_rejected;
      if (rejected?.reviewed_at) {
        const rejectedAt = Date.parse(rejected.reviewed_at);
        const attemptStartedAt = Date.parse(reverseSms.submitted_at || reverseSms.client_started_at || '') || 0;
        if (Number.isFinite(rejectedAt) && rejectedAt >= attemptStartedAt) {
          clearStoredPhoneVerificationAttempt(user);
          setVerificationError(rejected.rejection_reason || 'This phone number could not be verified.');
          setVerificationPhase('failed');
        }
      }
    } finally {
      trackingInFlightRef.current = false;
    }
  }, [checkAuth, fetchVerifyStatus, reverseSms, user, verificationPhase]);

  useEffect(() => {
    if (!reverseSms || verificationPhase !== 'waiting') return undefined;
    let cancelled = false;
    let timer;
    const poll = async () => {
      await checkVerification();
      if (!cancelled) timer = window.setTimeout(poll, VERIFICATION_POLL_MS);
    };
    timer = window.setTimeout(poll, VERIFICATION_POLL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [checkVerification, reverseSms, verificationPhase]);

  useEffect(() => {
    if (!reverseSms || verificationPhase !== 'waiting') return undefined;
    const checkWhenVisible = () => {
      if (document.visibilityState === 'visible') void checkVerification();
    };
    document.addEventListener('visibilitychange', checkWhenVisible);
    window.addEventListener('focus', checkWhenVisible);
    return () => {
      document.removeEventListener('visibilitychange', checkWhenVisible);
      window.removeEventListener('focus', checkWhenVisible);
    };
  }, [checkVerification, reverseSms, verificationPhase]);

  useEffect(() => {
    if (!verifyPopupOpen || typeof document === 'undefined') return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [verifyPopupOpen]);

  const openSmsApp = () => {
    if (!reverseSms?.code) {
      toast.error('Generate a code first');
      return;
    }
    const to = cleanSmsNumber(reverseSms.receiving_number || verifyStatus?.reverse_sms_receiving_number || '');
    if (!to) {
      toast.error('Receiving number missing');
      return;
    }
    const separator = /iPad|iPhone|iPod/i.test(navigator.userAgent || '') ? '&' : '?';
    openExternalUrl(`sms:${to}${separator}body=${encodeURIComponent(reverseSms.code)}`);
  };

  const copySmsBundle = async () => {
    if (!reverseSms?.code) return;
    const to = reverseSms.receiving_number || verifyStatus?.reverse_sms_receiving_number || '';
    await copyText(`Send ${reverseSms.code} to ${to}`, 'SMS details');
  };

  const openVerifyPopup = () => {
    if (activeReverseAttempt) {
      openVerificationScreen();
      return;
    }
    void startReverseSms();
  };

  const returnToPhoneEntry = () => {
    closeVerificationScreen(() => {
      verificationGenerationRef.current += 1;
      clearStoredPhoneVerificationAttempt(user);
      setReverseSms(null);
      setVerificationPhase('entry');
      setVerificationError('');
      setTrackingState('idle');
      window.setTimeout(() => phoneInputRef.current?.focus?.(), 80);
    });
  };

  const finishVerification = () => {
    closeVerificationScreen(() => {
      verificationGenerationRef.current += 1;
      clearStoredPhoneVerificationAttempt(user);
      setReverseSms(null);
      setVerificationPhase('entry');
      setTrackingState('idle');
      close?.();
    });
  };

  const phoneEntryControl = (
    <div ref={phoneSectionRef} data-testid="phone-verification-entry">
      <label className="text-xs uppercase tracking-wider mb-1.5 block" style={{ color: PANEL_ACCENT_TEXT }}>
        Phone number
      </label>
      <div className="flex items-center gap-2">
        <span
          className="shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center"
          style={{
            background: phoneVerified
              ? 'linear-gradient(135deg, #34D399, #16A34A)'
              : 'linear-gradient(135deg, #38BDF8, #2563EB)',
            boxShadow: phoneVerified
              ? '0 0 18px rgba(52,211,153,0.45), inset 0 1px 0 rgba(255,255,255,0.28)'
              : '0 0 18px rgba(56,189,248,0.48), inset 0 1px 0 rgba(255,255,255,0.28)',
          }}
          aria-hidden
        >
          {phoneVerified
            ? <CheckCircle2 className="w-6 h-6 text-white" strokeWidth={2.8} />
            : <Phone className="w-6 h-6 text-white" strokeWidth={2.5} />}
        </span>
        <div className="relative flex-1 min-w-0">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-black text-sky-200 select-none">+1</span>
          <Input
            ref={phoneInputRef}
            value={formatUsPhone(phoneDigits)}
            onChange={e => {
              if (!phoneVerified && !activeReverseAttempt) setPhoneDigits(toUsDigits(e.target.value));
            }}
            placeholder="Enter your 10 digit phone no"
            className={`pl-11 ${phoneVerified || (!initialVerifyOpen && verifyStatus?.reverse_sms_enabled !== false) ? 'pr-24' : 'pr-3'}`}
            style={inputStyle}
            inputMode="numeric"
            type="tel"
            readOnly={phoneVerified || activeReverseAttempt}
            aria-readonly={phoneVerified || activeReverseAttempt}
            data-testid="edit-phone"
          />
          {phoneVerified ? (
            <span
              className="absolute right-2 top-1/2 -translate-y-1/2 h-8 px-2 rounded-lg flex items-center gap-1 text-[11px] font-bold bg-emerald-500/[0.15] text-emerald-200 border border-emerald-400/30"
              data-testid="edit-phone-verified"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Verified
            </span>
          ) : !initialVerifyOpen && verifyStatus?.reverse_sms_enabled !== false ? (
            <button
              type="button"
              onClick={openVerifyPopup}
              disabled={reverseBusy || restoreCheckPending}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 px-3 rounded-lg text-[11px] font-black bg-sky-400 text-black shadow-[0_0_14px_rgba(56,189,248,0.45)] disabled:opacity-60"
              data-testid="phone-verify-open"
            >
              {activeReverseAttempt
                ? 'Resume'
                : restoreCheckPending
                  ? 'Checking…'
                  : reverseBusy
                    ? 'Starting…'
                    : 'Verify'}
            </button>
          ) : null}
        </div>
      </div>
      {!phoneVerified && (
        <p className="mt-2 text-[11px] leading-relaxed text-white/[0.55]">
          {activeReverseAttempt
            ? 'A secure code is active for this number. Resume the live tracker to finish verification.'
            : 'Use the US number that will send the verification text. It is saved to your profile only after verification.'}
        </p>
      )}
    </div>
  );

  return (
    <div className="space-y-3" data-testid="edit-profile-panel">
      {initialVerifyOpen ? (
        phoneVerified ? (
          <div className="py-3 text-center" data-testid="phone-verification-already-verified">
            <span
              className="mx-auto flex h-20 w-20 items-center justify-center rounded-full"
              style={{
                background: 'linear-gradient(135deg, rgba(52,211,153,0.28), rgba(22,163,74,0.18))',
                border: '1px solid rgba(52,211,153,0.46)',
                boxShadow: '0 0 34px rgba(52,211,153,0.28)',
              }}
            >
              <CheckCircle2 className="h-10 w-10 text-emerald-300" strokeWidth={2.6} />
            </span>
            <h3 className="display mt-4 text-xl font-black text-white">Phone verified</h3>
            <p className="mt-1 text-sm text-white/60">Your verified number is shown in your profile.</p>
            <div className="mt-4">{phoneEntryControl}</div>
            <Button type="button" onClick={close} className="mt-5 h-12 w-full bg-emerald-400 font-black text-emerald-950 hover:bg-emerald-300">
              Done
            </Button>
          </div>
        ) : (
          <div className="py-1">
            <div className="mb-5 flex items-start gap-3">
              <span
                className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl"
                style={{
                  background: 'linear-gradient(135deg, #38BDF8, #2563EB)',
                  boxShadow: '0 0 22px rgba(56,189,248,0.5), inset 0 1px 0 rgba(255,255,255,0.28)',
                }}
              >
                <Phone className="h-7 w-7 text-white" strokeWidth={2.6} />
              </span>
              <div>
                <h3 className="display text-xl font-black text-white">Verify your phone</h3>
                <p className="mt-1 text-xs leading-relaxed text-sky-100/70">
                  Enter your number first. We will create a secure code on the next screen.
                </p>
              </div>
            </div>
            {phoneEntryControl}
            <div className="mt-4 rounded-2xl p-3" style={{ background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.22)' }}>
              <div className="flex items-start gap-2 text-xs leading-relaxed text-sky-100/80">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-sky-300" />
                United States numbers only. The number entered here must be the number that sends the text.
              </div>
            </div>
            <Button
              type="button"
              onClick={openVerifyPopup}
              disabled={
                restoreCheckPending
                || (!activeReverseAttempt && (
                  reverseBusy
                  || phoneDigits.length !== 10
                  || verifyStatus?.reverse_sms_enabled === false
                ))
              }
              className="mt-4 h-12 w-full bg-sky-400 font-black text-black hover:bg-sky-300 disabled:opacity-50"
              data-testid="phone-verify-submit"
            >
              {restoreCheckPending
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Checking active verification…</>
                : reverseBusy
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating secure code…</>
                : activeReverseAttempt
                  ? <><Wifi className="mr-2 h-4 w-4" /> Resume live verification</>
                  : <><Send className="mr-2 h-4 w-4" /> Verify &amp; get code</>}
            </Button>
            {verifyStatusLoading && verifyStatus === null && (
              <p className="mt-2 text-center text-[11px] text-white/[0.45]">Checking verification service…</p>
            )}
            {verifyStatusError && verifyStatus === null && (
              <button
                type="button"
                onClick={() => { void refreshVerifyStatus(); }}
                className="mx-auto mt-2 flex items-center gap-1.5 text-[11px] font-bold text-amber-200 underline decoration-amber-300/40 underline-offset-4"
                data-testid="phone-verification-status-retry"
              >
                <RefreshCw className="h-3.5 w-3.5" /> Could not check status · Retry
              </button>
            )}
            {verifyStatus && !verifyStatus.reverse_sms_enabled && (
              <p className="mt-2 text-center text-[11px] text-amber-200/80">Phone verification is temporarily unavailable.</p>
            )}
          </div>
        )
      ) : (
        <>
          <div>
            <label className="text-xs uppercase tracking-wider mb-1 block" style={{ color: PANEL_ACCENT_TEXT }}>Full name</label>
            <Input value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Your full name" style={inputStyle} data-testid="edit-fullname" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider mb-1 block" style={{ color: PANEL_ACCENT_TEXT }}>Username</label>
            <div className="relative">
              <Input
                value={username}
                onChange={e => setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                placeholder="preferred_name"
                style={inputStyle}
                maxLength={24}
                disabled={!!currentUsername}
                readOnly={!!currentUsername}
                data-testid="edit-username"
              />
              {usernameChanged && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2">
                  {usernameChecking ? <div className="spinner w-4 h-4" /> :
                    usernameAvail === true ? <CheckCircle2 className="w-5 h-5 text-green-400" data-testid="username-avail-ok" /> :
                    usernameAvail === false ? <X className="w-5 h-5 text-red-400" data-testid="username-avail-bad" /> : null}
                </span>
              )}
            </div>
            <p className="text-[10px] mt-1" style={{ color: usernameAvail === false ? '#F87171' : 'rgba(255,255,255,0.72)' }}>
              {currentUsername ? 'Username is locked once set. Tap it on your profile to copy it.' :
               usernameChanged && usernameAvail === false ? 'Taken or invalid — use 3-24 letters / digits / _' :
               usernameChanged && usernameAvail === true ? 'Available! Save to claim it.' :
               'Pick a unique handle. You cannot change it after saving.'}
            </p>
          </div>
          {phoneEntryControl}
          {phoneVerified && (
            <div className="rounded-lg p-2 text-[11px] flex items-center gap-2" style={{ background: PANEL_ACCENT_DIM, color: '#FFFFFF', border: `1px solid ${PANEL_ACCENT_BORDER}` }}>
              <CheckCircle2 className="w-4 h-4" /> Phone verified
            </div>
          )}
          <div className="rounded-lg p-2 text-[11px]" style={{ background: PANEL_GOLD_DIM, color: '#FFFFFF', border: `1px solid ${PANEL_GOLD_BORDER}`, boxShadow: `0 0 18px color-mix(in oklab, ${PANEL_GOLD} 16%, transparent)` }}>
            Email cannot be changed here. To reset your password, use Reset Pass in the profile menu.
          </div>
          <Button onClick={submit} disabled={busy || (usernameChanged && usernameAvail === false)} className="btn-primary w-full h-11 display font-bold mt-1" data-testid="edit-profile-submit">
            {busy ? <div className="spinner w-4 h-4" /> : <><BadgeCheck className="w-4 h-4 mr-2" /> Save changes</>}
          </Button>
        </>
      )}

      {verifyPopupOpen && reverseSms && (
      <Dialog
        open
        onOpenChange={(open) => {
          if (!open) closeVerificationScreen();
        }}
      >
        <DialogContent
          overlayClassName="!z-[9999] !bg-black/[0.85] backdrop-blur-sm"
          className="!fixed !inset-0 !left-0 !top-0 z-[10000] !h-[100dvh] !max-h-[100dvh] !w-screen !max-w-none !translate-x-0 !translate-y-0 overflow-y-auto overscroll-contain rounded-none border-0 p-0 shadow-none sm:rounded-none [&>button]:hidden"
          style={{
            background: 'radial-gradient(circle at 50% 22%, rgba(14,116,144,0.34), transparent 38%), linear-gradient(180deg, #06111D 0%, #01060B 100%)',
            color: '#FFFFFF',
          }}
          data-testid="phone-verification-code-screen"
        >
          <DialogTitle className="sr-only">Phone verification</DialogTitle>
          <div className="mx-auto flex min-h-[100dvh] w-full max-w-md flex-col px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-[max(0.75rem,env(safe-area-inset-top))] sm:px-6">
            <div className="sticky top-0 z-10 flex items-center justify-between py-2" style={{ background: 'linear-gradient(180deg, rgba(6,17,29,0.98), rgba(6,17,29,0))' }}>
              <button
                type="button"
                onClick={() => closeVerificationScreen()}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-white/[0.08] transition active:scale-95 hover:bg-white/[0.12]"
                aria-label="Back to phone number"
                data-testid="phone-verification-back"
              >
                <ChevronLeft className="h-6 w-6 text-white" />
              </button>
              <p className="display text-sm font-extrabold tracking-wide text-white">Phone verification</p>
              <span className="h-11 w-11" aria-hidden />
            </div>

            {verificationPhase === 'verified' ? (
              <div className="flex flex-1 flex-col items-center justify-center py-10 text-center" data-testid="phone-verification-success">
                <div
                  className="relative flex h-40 w-40 items-center justify-center rounded-full"
                  style={{
                    background: 'radial-gradient(circle, rgba(52,211,153,0.28), rgba(22,163,74,0.08) 66%, transparent 67%)',
                    boxShadow: '0 0 70px rgba(52,211,153,0.34)',
                  }}
                >
                  <span className="absolute inset-2 rounded-full border-2 border-emerald-300/[0.35] animate-ping" style={{ animationDuration: '2.4s' }} />
                  <span className="flex h-24 w-24 items-center justify-center rounded-full bg-emerald-400 text-emerald-950 shadow-[0_0_34px_rgba(52,211,153,0.58)]">
                    <CheckCircle2 className="h-14 w-14" strokeWidth={2.7} />
                  </span>
                </div>
                <h2 className="display mt-7 text-3xl font-black text-white">Phone verified!</h2>
                <p className="mt-2 max-w-xs text-sm leading-relaxed text-white/[0.65]">
                  <span className="font-bold text-emerald-300">
                    +1 {formatUsPhone(reverseSms.phone_e164 || phoneDigits)}
                  </span> is now secured and saved to your profile.
                </p>
                <Button type="button" onClick={finishVerification} className="mt-8 h-[3.25rem] w-full bg-emerald-400 text-base font-black text-emerald-950 hover:bg-emerald-300">
                  Done
                </Button>
              </div>
            ) : ['expired', 'failed'].includes(verificationPhase) ? (
              <div className="flex flex-1 flex-col items-center justify-center py-10 text-center" data-testid={`phone-verification-${verificationPhase}`}>
                <span className="flex h-24 w-24 items-center justify-center rounded-full border border-amber-300/30 bg-amber-400/10 shadow-[0_0_34px_rgba(251,191,36,0.18)]">
                  {verificationPhase === 'expired'
                    ? <Clock className="h-11 w-11 text-amber-300" />
                    : <ShieldCheck className="h-11 w-11 text-rose-300" />}
                </span>
                <h2 className="display mt-6 text-2xl font-black text-white">
                  {verificationPhase === 'expired' ? 'Code expired' : 'Could not verify'}
                </h2>
                <p className="mt-2 max-w-xs text-sm leading-relaxed text-white/60">{verificationError}</p>
                <Button type="button" onClick={returnToPhoneEntry} className="mt-7 h-12 w-full bg-sky-400 font-black text-black hover:bg-sky-300">
                  <RefreshCw className="mr-2 h-4 w-4" /> Enter number &amp; try again
                </Button>
              </div>
            ) : (
              <div className="flex flex-1 flex-col py-4 sm:justify-center sm:py-8">
                <div className="text-center">
                  <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-sky-300">Text this secure code</p>
                  <h2 className="display mt-2 text-2xl font-black text-white">Send it from your phone</h2>
                  <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-white/[0.58]">
                    Send it to the {inboxBrandName} inbox from <span className="font-bold text-white">+1 {formatUsPhone(phoneDigits)}</span>.
                  </p>
                </div>

                <div className="relative mx-auto mt-7 flex h-56 w-56 items-center justify-center" data-testid="phone-verification-code">
                  <span
                    className="absolute inset-0 rounded-full border-[3px] border-transparent border-r-violet-400 border-t-sky-300 animate-spin"
                    style={{ animationDuration: '3.2s', filter: 'drop-shadow(0 0 10px rgba(56,189,248,0.7))' }}
                    aria-hidden
                  />
                  <span
                    className="absolute inset-3 rounded-full border-2 border-transparent border-b-cyan-300/70 border-l-sky-200/40 animate-spin"
                    style={{ animationDuration: '5.8s', animationDirection: 'reverse' }}
                    aria-hidden
                  />
                  <button
                    type="button"
                    onClick={() => copyText(reverseSms.code, 'Code')}
                    className="relative flex h-44 w-44 flex-col items-center justify-center rounded-full border border-white/[0.12] bg-black/[0.35] shadow-[inset_0_0_30px_rgba(56,189,248,0.12),0_0_35px_rgba(56,189,248,0.18)]"
                    aria-label="Copy verification code"
                  >
                    <span className="font-mono text-[2.45rem] font-black tracking-[0.18em] text-sky-100" data-testid="phone-verification-code-value">
                      {reverseSms.code}
                    </span>
                    <span className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white/[0.42]">
                      <Copy className="h-3 w-3" /> Tap to copy
                    </span>
                  </button>
                </div>

                <div className="mt-5 grid grid-cols-2 gap-2">
                  <div className="rounded-2xl border border-white/10 bg-white/[0.045] p-3 text-center">
                    <div className="flex items-center justify-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-white/[0.42]">
                      <Clock className="h-3.5 w-3.5" /> Time left
                    </div>
                    <p className="mt-1 font-mono text-xl font-black text-amber-200" data-testid="phone-verification-countdown">
                      {formatCountdown(secondsRemaining)}
                    </p>
                  </div>
                  <div
                    className={`rounded-2xl border p-3 text-center ${
                      trackingState === 'reconnecting'
                        ? 'border-amber-300/20 bg-amber-400/[0.07]'
                        : 'border-emerald-300/[0.15] bg-emerald-400/[0.06]'
                    }`}
                    data-testid="phone-verification-tracking"
                    data-tracking-state={trackingState}
                  >
                    <div className={`flex items-center justify-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] ${
                      trackingState === 'reconnecting' ? 'text-amber-200/80' : 'text-emerald-200/70'
                    }`}>
                      {trackingState === 'reconnecting'
                        ? <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                        : <Wifi className="h-3.5 w-3.5 animate-pulse" />}
                      {trackingState === 'reconnecting' ? 'API retry' : 'Live API'}
                    </div>
                    <p className={`mt-1 text-xs font-extrabold ${
                      trackingState === 'reconnecting' ? 'text-amber-300' : 'text-emerald-300'
                    }`}>
                      {trackingState === 'reconnecting'
                        ? 'Reconnecting'
                        : trackingState === 'connecting'
                          ? 'Connecting'
                          : 'Tracking SMS'}
                    </p>
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-black/25 p-3">
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-[0.17em] text-white/[0.38]">Send code to</p>
                    <p className="mt-1 truncate font-mono text-base font-black text-white">{reverseSms.receiving_number}</p>
                  </div>
                  <button type="button" onClick={() => copyText(reverseSms.receiving_number, 'Receiving number')} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 hover:bg-white/[0.15]" aria-label="Copy receiving number">
                    <Copy className="h-5 w-5 text-white" />
                  </button>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Button type="button" onClick={copySmsBundle} className="h-12 bg-white/10 font-bold text-white hover:bg-white/[0.15]">
                    <Copy className="mr-2 h-4 w-4" /> Copy all
                  </Button>
                  <Button type="button" onClick={openSmsApp} className="h-12 bg-sky-400 font-black text-black hover:bg-sky-300" data-testid="reverse-sms-open-app">
                    <MessageCircle className="mr-2 h-4 w-4" /> Open SMS
                  </Button>
                </div>

                <div className="mt-4 rounded-2xl border border-sky-300/[0.15] bg-sky-400/[0.06] px-3 py-2.5 text-center">
                  <p className="text-[11px] leading-relaxed text-sky-100/[0.62]">
                    {trackingState === 'reconnecting'
                      ? 'Live status is temporarily unavailable. Retrying automatically; your code remains active.'
                      : trackingState === 'connecting'
                        ? 'Connecting to live verification status…'
                        : (
                          <>
                            Waiting for your text. Verification updates automatically
                            {lastTrackingAt ? ` · checked ${new Date(lastTrackingAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' })}` : ''}.
                          </>
                        )}
                  </p>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      )}
    </div>
  );
}
